/**
 * Martina London — Product detail page
 * Handles variant pill selection, tab switching, size-guide modal,
 * stock indicator, mobile gallery pagination dots, and the mobile
 * sticky add-to-bag visibility.
 * Vanilla JS, no dependencies. Loaded with `defer`.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-product]');
  if (!root) return;

  const productData = (() => {
    const node = root.querySelector('[data-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  })();

  /* ----------------------------------------------------------
   * Variant selection
   * -------------------------------------------------------- */
  const form = root.querySelector('[data-product-form]');
  const variantInput = root.querySelector('[data-variant-id]');
  const submitBtns = root.querySelectorAll('[data-product-submit]');
  const priceEl = root.querySelector('[data-product-price]');
  const stockEl = root.querySelector('[data-product-stock]');

  const optionGroups = root.querySelectorAll('[data-option-group]');

  const getSelectedOptions = () => {
    const selected = [];
    optionGroups.forEach((group) => {
      const active = group.querySelector('.product__pill.is-selected');
      selected.push(active ? active.dataset.optionValue : null);
    });
    return selected;
  };

  const findVariant = (selected) => {
    if (!productData || !productData.variants) return null;
    return productData.variants.find((v) =>
      v.options.every((opt, i) => opt === selected[i])
    );
  };

  const formatMoney = (cents) => {
    if (!productData || !productData.money_format) return cents;
    const amount = (cents / 100).toFixed(2);
    return productData.money_format.replace(/\{\{\s*amount\s*\}\}/, amount);
  };

  const updateAvailabilityForGroup = (groupIndex) => {
    if (!productData) return;
    const otherSelected = getSelectedOptions();
    optionGroups.forEach((group, gi) => {
      if (gi !== groupIndex) return;
      group.querySelectorAll('.product__pill').forEach((pill) => {
        const value = pill.dataset.optionValue;
        const trial = otherSelected.slice();
        trial[gi] = value;
        const match = productData.variants.find((v) =>
          v.options.every((opt, i) =>
            trial[i] == null ? true : opt === trial[i]
          )
        );
        const soldOut = !match || !match.available;
        pill.classList.toggle('is-sold-out', soldOut && !pill.classList.contains('is-selected'));
      });
    });
  };

  const syncFromVariant = () => {
    const selected = getSelectedOptions();
    const variant = findVariant(selected);

    if (variantInput) variantInput.value = variant ? variant.id : '';

    submitBtns.forEach((btn) => {
      if (selected.some((v) => v == null)) {
        btn.disabled = true;
        btn.textContent = btn.dataset.labelSelect || 'Select size';
      } else if (!variant || !variant.available) {
        btn.disabled = true;
        btn.textContent = btn.dataset.labelSoldOut || 'Sold out';
      } else {
        btn.disabled = false;
        btn.textContent = btn.dataset.labelAdd || 'Add to bag';
      }
    });

    if (priceEl && variant) {
      const price = formatMoney(variant.price);
      const compare =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price)
          : null;
      priceEl.innerHTML = compare
        ? `<del>${compare}</del><ins>${price}</ins>`
        : price;
    }

    if (stockEl) {
      const inStock = variant && variant.available;
      stockEl.hidden = !inStock;
    }
  };

  optionGroups.forEach((group, gi) => {
    group.addEventListener('click', (e) => {
      const pill = e.target.closest('.product__pill');
      if (!pill || pill.disabled) return;
      // Defensive — pills are type="button" so they shouldn't submit
      // the surrounding form, but guard anyway in case a future markup
      // change (or a third-party script) tries to.
      e.preventDefault();
      // Clear selection state from siblings (both class and ARIA)
      // and apply it to the clicked pill.
      group.querySelectorAll('.product__pill').forEach((p) => {
        p.classList.remove('is-selected');
        p.setAttribute('aria-checked', 'false');
      });
      pill.classList.add('is-selected');
      pill.setAttribute('aria-checked', 'true');
      // Sync availability for sibling groups based on this selection.
      optionGroups.forEach((_, otherIndex) => {
        if (otherIndex !== gi) updateAvailabilityForGroup(otherIndex);
      });
      syncFromVariant();
      swapGalleryToVariant();
    });
  });

  // Initial sync (Shopify renders selected_or_first_available_variant).
  syncFromVariant();

  /* ----------------------------------------------------------
   * Tabs (ARIA tabs pattern)
   * -------------------------------------------------------- */
  const tabs = Array.from(root.querySelectorAll('[data-product-tab]'));

  const activateTab = (tab) => {
    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
      const panelId = t.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) panel.hidden = !selected;
    });
  };

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', (e) => {
      // Tabs sit inside the product <form> after the recent reorder.
      // Even though each tab carries type="button", suppress the event
      // defensively so a misbehaving environment (browser quirk,
      // third-party script binding to form clicks) cannot intercept
      // the click and reload the page.
      e.preventDefault();
      e.stopPropagation();
      activateTab(tab);
    });
    tab.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      let nextIndex = i;
      if (e.key === 'ArrowRight') nextIndex = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') nextIndex = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') nextIndex = 0;
      else if (e.key === 'End') nextIndex = tabs.length - 1;
      tabs[nextIndex].focus();
      activateTab(tabs[nextIndex]);
    });
  });

  /* ----------------------------------------------------------
   * Size guide modal (placeholder)
   * -------------------------------------------------------- */
  const sizeGuideModal = document.querySelector('[data-size-guide-modal]');

  const openSizeGuide = () => {
    if (!sizeGuideModal) return;
    sizeGuideModal.classList.add('is-open');
    sizeGuideModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeSizeGuide = () => {
    if (!sizeGuideModal) return;
    sizeGuideModal.classList.remove('is-open');
    sizeGuideModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  document.querySelectorAll('[data-size-guide-open]').forEach((btn) => {
    btn.addEventListener('click', openSizeGuide);
  });

  document.querySelectorAll('[data-size-guide-close]').forEach((btn) => {
    btn.addEventListener('click', closeSizeGuide);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sizeGuideModal && sizeGuideModal.classList.contains('is-open')) {
      closeSizeGuide();
    }
  });

  /* ----------------------------------------------------------
   * Mobile gallery — pagination dots
   * -------------------------------------------------------- */
  const track = root.querySelector('[data-gallery-track]');
  const dots = root.querySelectorAll('[data-gallery-dot]');

  if (track && dots.length) {
    let activeDot = 0;
    const updateActive = () => {
      const slideWidth = track.clientWidth;
      if (!slideWidth) return;
      const idx = Math.round(track.scrollLeft / slideWidth);
      if (idx === activeDot) return;
      activeDot = idx;
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
    };
    track.addEventListener('scroll', updateActive, { passive: true });
    updateActive();
  }

  /* ----------------------------------------------------------
   * Desktop gallery — counter + thumbnail navigation.
   * Counter tracks which image is most-visible; thumb click
   * scrolls the matching image into view.
   * -------------------------------------------------------- */
  const galleryCurrent = root.querySelector('[data-gallery-current]');
  const galleryItems = root.querySelectorAll('[data-gallery-item]');
  const galleryThumbs = root.querySelectorAll('[data-gallery-thumb]');

  const setActiveGalleryIndex = (index) => {
    if (galleryCurrent) galleryCurrent.textContent = String(index + 1);
    galleryThumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
    });
  };

  const swapGalleryToVariant = () => {
    const selected = getSelectedOptions();
    const variant = findVariant(selected);
    if (!variant || !variant.featured_image_id) return;
    const imageId = String(variant.featured_image_id);
    const target = Array.from(galleryItems).find(
      (item) => item.dataset.imageId === imageId
    );
    if (!target) return;
    const idx = parseInt(target.dataset.galleryIndex, 10);
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (isDesktop) {
      // Stacked gallery: scroll the item into view within the page
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Horizontal carousel: scroll the track to the item's offset
      if (track) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
    if (!Number.isNaN(idx)) setActiveGalleryIndex(idx);
  };

  if (galleryItems.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = parseInt(entry.target.dataset.galleryIndex, 10);
            if (!Number.isNaN(idx)) setActiveGalleryIndex(idx);
          }
        });
      },
      { threshold: [0.5] }
    );
    galleryItems.forEach((item) => {
      if (item.dataset.galleryIndex != null) observer.observe(item);
    });
  }

  // Auto-play gallery videos when scrolled into view; pause when out.
  const galleryVideos = root.querySelectorAll('[data-gallery-video]');
  if (galleryVideos.length && 'IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.play().catch(() => {});
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.1 }
    );
    galleryVideos.forEach((v) => videoObserver.observe(v));
  }

  galleryThumbs.forEach((thumb) => {
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      const idx = parseInt(thumb.dataset.galleryIndex, 10);
      const target = galleryItems[idx];
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  /* ----------------------------------------------------------
   * Mobile sticky add-to-bag — show after the inline button
   * scrolls out of view.
   * -------------------------------------------------------- */
  const stickyCta = root.querySelector('[data-sticky-cta]');
  const inlineSubmit = root.querySelector('[data-product-submit-inline]');

  if (stickyCta && inlineSubmit && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyCta.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(inlineSubmit);
  }

  /* ----------------------------------------------------------
   * Lightbox
   * -------------------------------------------------------- */
  const lightbox = document.querySelector('[data-lightbox]');

  if (lightbox) {
    const lightboxImage = lightbox.querySelector('[data-lightbox-image]');
    const lightboxCounter = lightbox.querySelector('[data-lightbox-counter]');
    const triggers = Array.from(document.querySelectorAll('[data-lightbox-open]'));
    let currentIndex = 0;

    lightbox.dataset.count = String(triggers.length);

    const updateImage = (index) => {
      const trigger = triggers[index];
      if (!trigger) return;
      const src = trigger.dataset.fullSrc || '';
      const img = trigger.querySelector('img');
      lightboxImage.src = src;
      lightboxImage.alt = img ? img.alt : '';
      if (lightboxCounter) lightboxCounter.textContent = `${index + 1} / ${triggers.length}`;
      currentIndex = index;
    };

    const openLightbox = (index) => {
      updateImage(index);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
      const closeBtn = lightbox.querySelector('.product-lightbox__close');
      if (closeBtn) closeBtn.focus();
    };

    const closeLightbox = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lightbox-open');
      if (triggers[currentIndex]) triggers[currentIndex].focus();
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const idx = parseInt(trigger.dataset.lightboxIndex, 10);
        openLightbox(Number.isNaN(idx) ? 0 : idx);
      });
    });

    lightbox.querySelectorAll('[data-lightbox-close]').forEach((el) => {
      el.addEventListener('click', closeLightbox);
    });

    const prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    const nextBtn = lightbox.querySelector('[data-lightbox-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        openLightbox((currentIndex - 1 + triggers.length) % triggers.length);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        openLightbox((currentIndex + 1) % triggers.length);
      });
    }

    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); closeLightbox(); return; }
      if (e.key === 'ArrowLeft') openLightbox((currentIndex - 1 + triggers.length) % triggers.length);
      if (e.key === 'ArrowRight') openLightbox((currentIndex + 1) % triggers.length);
    });
  }
})();
